<template>
  <div
    v-if="visible"
    class="
      fixed inset-0 z-50 flex items-center justify-center
      bg-black bg-opacity-40 px-4
    ">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
      <div class="flex flex-col space-y-5">
        <div class="flex flex-col space-y-2">
          <h3 class="text-20px leading-24px font-bold text-primary">
            Подтверждение телефона
          </h3>

          <p class="text-14px leading-20px text-muted">
            Подтвердите номер телефона через SMS Aero Mobile ID.
          </p>

          <p class="text-14px leading-20px text-inverse">
            {{ phone }}
          </p>
        </div>

        <div
          v-if="statusMessage"
          class="text-14px leading-20px text-inverse">
          {{ statusMessage }}
        </div>

        <div
          v-if="errorMessage"
          class="text-14px leading-20px text-red-600">
          {{ errorMessage }}
        </div>

        <div
          v-if="otpRequired"
          class="flex flex-col space-y-3">
          <label class="text-14px leading-20px font-semibold text-primary">
            Код подтверждения
          </label>

          <input
            v-model="otpCode"
            class="
              w-full border border-geyser rounded px-4 py-3
              text-18px leading-24px tracking-widest text-center
            "
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="4"
            placeholder="0000"
            :disabled="loading"
            @input="onOtpInput">

          <button
            class="btn bg-primary border-solid border-primary text-white"
            type="button"
            :disabled="loading || otpCode.length !== 4"
            @click="submitOtp">
            Подтвердить код
          </button>
        </div>

        <div class="flex flex-col space-y-3">
          <button
            v-if="canFallback"
            class="btn bg-white border-solid border-primary text-primary"
            type="button"
            :disabled="loading"
            @click="fallbackToSms">
            Получить обычный SMS-код
          </button>

          <div
            v-else
            class="
              rounded-lg border border-geyser bg-hint px-4 py-3
              text-12px leading-18px text-muted
            ">
            Сейчас выполняется подтверждение через Mobile ID.
            Обычный SMS-код будет доступен, если Mobile ID не сработает
            или время ожидания истечёт.
          </div>

          <button
            class="btn bg-white border-solid border-geyser text-primary"
            type="button"
            @click="close">
            Отмена
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MobileID from 'smsaero-mobileid-sdk';
import { axiosInstance } from '@/api/axios';

const FALLBACK_DELAY_MS = 45000;

export default {
  name: 'MobileIdVerification',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    phone: {
      type: String,
      required: true,
    },
    tokenUrl: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      mid: null,
      loading: false,
      otpRequired: false,
      otpCode: '',
      statusMessage: '',
      errorMessage: '',
      canFallback: false,
      started: false,
      fallbackTimer: null,
      errorHandled: false,
    };
  },

  computed: {
    effectiveTokenUrl() {
      const rawTokenUrl = String(this.tokenUrl || '').trim();

      if (/^https?:\/\//i.test(rawTokenUrl)) {
        return rawTokenUrl;
      }

      const baseURL = String(axiosInstance.defaults.baseURL || '')
        .replace(/\/$/, '');
      const tokenPath = rawTokenUrl || '/contractor/mobile-id/token';
      const normalizedTokenPath = tokenPath.startsWith('/')
        ? tokenPath
        : `/${tokenPath}`;

      return `${baseURL}${normalizedTokenPath}`;
    },
  },

  watch: {
    visible(value) {
      if (value) {
        this.start();
      } else {
        this.reset();
      }
    },
  },

  beforeDestroy() {
    this.reset();
  },

  methods: {
    async start() {
      if (this.loading || this.started) {
        return;
      }

      this.reset();

      this.loading = true;
      this.started = true;
      this.canFallback = false;
      this.statusMessage = 'Запускаем подтверждение телефона...';

      try {
        this.mid = new MobileID({
          tokenUrl: this.effectiveTokenUrl,
        });

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('[iWALY] Mobile ID tokenUrl:', this.effectiveTokenUrl);
        }

        this.bindEvents();
        this.startFallbackTimer();

        await this.mid.init();

        this.loading = false;
        this.statusMessage = 'Ожидаем подтверждение телефона...';

        await this.mid.start(this.phone);
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loading = false;
      }
    },

    bindEvents() {
      if (!this.mid || typeof this.mid.on !== 'function') {
        return;
      }

      this.mid.on('ready', () => {
        this.loading = false;
        this.statusMessage = 'Сессия подтверждения создана.';
      });

      this.mid.on('pending', () => {
        this.loading = false;
        this.statusMessage = 'Ожидаем подтверждение телефона...';
      });

      this.mid.on('otp_required', () => {
        this.loading = false;
        this.otpRequired = true;
        this.errorMessage = '';
        this.statusMessage = 'Введите 4-значный код SMS Aero Mobile ID.';
      });

      this.mid.on('verified', (payload = {}) => {
        this.clearFallbackTimer();

        const sessionId = typeof this.mid.getSessionId === 'function'
          ? this.mid.getSessionId()
          : payload.session_id;

        const verifyToken = payload.verify_token || payload.verifyToken || '';

        if (!sessionId || !verifyToken) {
          this.loading = false;
          this.errorMessage = 'Не удалось получить данные подтверждения.';
          this.statusMessage = 'Можно получить обычный SMS-код.';
          this.canFallback = true;
          return;
        }

        this.loading = true;
        this.canFallback = false;
        this.statusMessage = 'Телефон подтверждён. Завершаем регистрацию.';
        this.errorMessage = '';

        this.$emit('verified', {
          session_id: sessionId,
          verify_token: verifyToken,
        });
      });

      this.mid.on('rejected', () => {
        this.showFallbackNow(
          'Подтверждение отклонено.',
          'Можно получить обычный SMS-код.',
        );
      });

      this.mid.on('expired', () => {
        this.showFallbackNow(
          'Время подтверждения истекло.',
          'Можно получить обычный SMS-код.',
        );
      });

      this.mid.on('invalid_code', () => {
        this.loading = false;
        this.errorMessage = 'Неверный код подтверждения.';
        this.statusMessage = 'Введите корректный 4-значный код.';
      });

      this.mid.on('rate_limit', () => {
        this.showFallbackNow(
          'Слишком много попыток.',
          'Попробуйте получить обычный SMS-код.',
        );
      });

      this.mid.on('error', (error) => {
        this.handleError(error);
      });
    },

    startFallbackTimer() {
      this.clearFallbackTimer();

      this.fallbackTimer = setTimeout(() => {
        this.loading = false;
        this.canFallback = true;
        this.statusMessage = [
          'Mobile ID отвечает дольше обычного.',
          'Теперь можно получить обычный SMS-код.',
        ].join(' ');
      }, FALLBACK_DELAY_MS);
    },

    clearFallbackTimer() {
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
      }
    },

    showFallbackNow(errorMessage, statusMessage) {
      this.clearFallbackTimer();

      this.loading = false;
      this.errorMessage = errorMessage;
      this.statusMessage = statusMessage;
      this.canFallback = true;
    },

    onOtpInput() {
      this.otpCode = this.otpCode.replace(/\D/g, '').slice(0, 4);
    },

    async submitOtp() {
      if (!this.mid || !this.otpCode || this.otpCode.length !== 4) {
        return;
      }

      this.loading = true;
      this.errorMessage = '';
      this.statusMessage = 'Проверяем код...';

      try {
        await this.mid.submitOTP(this.otpCode);
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loading = false;
      }
    },

    fallbackToSms() {
      this.clearFallbackTimer();
      this.destroySdk();

      this.loading = true;
      this.errorMessage = '';
      this.statusMessage = 'Запрашиваем обычный SMS-код...';

      this.$emit('fallback');
    },

    close() {
      this.clearFallbackTimer();
      this.destroySdk();
      this.$emit('close');
    },

    reset() {
      this.clearFallbackTimer();
      this.destroySdk();

      this.loading = false;
      this.otpRequired = false;
      this.otpCode = '';
      this.statusMessage = '';
      this.errorMessage = '';
      this.canFallback = false;
      this.started = false;
      this.errorHandled = false;
    },

    destroySdk() {
      if (this.mid && typeof this.mid.destroy === 'function') {
        this.mid.destroy();
      }

      this.mid = null;
    },

    handleError(error) {
      if (this.errorHandled) {
        return;
      }

      this.errorHandled = true;

      const message = this.getErrorMessage(error);

      this.showFallbackNow(
        message,
        'Можно продолжить через обычное SMS-подтверждение.',
      );

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[iWALY] Mobile ID error', error);
      }
    },

    getErrorMessage(error) {
      const status = error && error.response
        ? error.response.status
        : null;

      const responseError = error && error.response && error.response.data
        ? error.response.data.error
        : null;

      const responseMessage = error && error.response && error.response.data
        ? error.response.data.message
        : null;

      if (responseError === 'phone_already_registered') {
        return [
          'Этот номер уже зарегистрирован в системе.',
          'Если вы уже пользовались iWALY, войдите в аккаунт.',
        ].join(' ');
      }

      if (responseError === 'invalid_phone') {
        return 'Номер телефона указан неверно.';
      }

      if (responseError === 'too_many_phone_code_requests' || status === 429) {
        return responseMessage || [
          'Слишком много запросов кода.',
          'Попробуйте позже.',
        ].join(' ');
      }

      if (status === 422) {
        return 'Проверьте номер телефона и попробуйте ещё раз.';
      }

      if (status === 503) {
        return 'Mobile ID временно недоступен. Можно получить обычный SMS-код.';
      }

      return 'Mobile ID недоступен. Можно получить обычный SMS-код.';
    },
  },
};
</script>
